import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { URLParamsType } from '../../types';
import { uploadFiles } from '../upload/uploadSlice';
import validateFiles, { UploadValidationError } from '../upload/fileValidation';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import logger from '../../lib/logger';
import useLimits from '../plan/hooks/limits';
import type { RootState } from '../../reducers';

interface UploadDropZoneProps {
  children: React.ReactNode;
}

export const UploadDropZone: React.FC<UploadDropZoneProps> = ({ children }) => {
  const { teamId, folderId } = useParams<URLParamsType>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const guardLimit = useLimits();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [draggedFileCount, setDraggedFileCount] = useState(0);
  const [showUploadAnimation, setShowUploadAnimation] = useState(false);
  const uploadStatus = useSelector((state: RootState) => state.upload.status);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const resetStates = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsUploading(false);
    setIsDragging(false);
    setShowUploadAnimation(false);
    setDraggedFileCount(0);
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      setDraggedFileCount(0);

      if (!teamId || !folderId) return;

      // Check if this is a file being dragged for chat context (not for upload)
      const chatFileData = e.dataTransfer.getData('application/json');
      if (chatFileData) {
        // This is a drag from library to chat, not a file upload
        return;
      }

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length === 0) return;

      setShowUploadAnimation(true);
      // Wait for animation to complete before showing upload state
      timeoutRef.current = setTimeout(() => {
        setShowUploadAnimation(false);
        setIsUploading(true);
      }, 400);

      const validationResult = await validateFiles(droppedFiles);

      if (!validationResult.isValid) {
        const { errorMessage } = validationResult as UploadValidationError;
        logger.error(new Error(errorMessage));
        dispatch(
          setAlertMessage({
            message: errorMessage || t('library.upload.errors.invalidFiles'),
            type: 'error',
          }),
        );
        resetStates();
        return;
      }

      try {
        if (droppedFiles.length > 1) {
          guardLimit('bulkUpload', () => {
            dispatch(uploadFiles({ teamId, folderId, files: droppedFiles }));
          });
        } else {
          dispatch(uploadFiles({ teamId, folderId, files: droppedFiles }));
        }
      } finally {
        resetStates();
      }
    },
    [teamId, folderId, dispatch, guardLimit, t],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    // Check if this is a file being dragged for chat context (not for upload)
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('application/json')) {
      // This is a drag from library to chat, don't show upload UI
      return;
    }

    setIsDragging(true);
    const fileCount = e.dataTransfer.items.length;
    if (fileCount > 0) {
      setDraggedFileCount(fileCount);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDraggedFileCount(0);
  }, []);

  const renderContent = () => {
    if (showUploadAnimation) {
      return <Upload className="w-20 h-20 mb-3 text-black/60 animate-pulse" />;
    }

    if (isUploading) {
      return (
        <>
          <div className="mb-3 w-15 h-15">
            <svg
              className="animate-spin h-15 w-15 text-black/60"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-medium tracking-wide text-black/70 animate-fadeIn">
            {t('library.upload.uploading')}
          </h2>
        </>
      );
    }

    return (
      <>
        <Upload className="w-20 h-20 mb-3 text-black/60 animate-float" />
        <h2 className="text-xl font-medium tracking-wide text-black/70 animate-fadeIn">
          {t('library.upload.dropFiles', { count: draggedFileCount })}
        </h2>
      </>
    );
  };

  return (
    <div
      className="h-full relative"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {children}
      <div
        className={cn(
          'transition-opacity duration-300',
          (isDragging || isUploading || showUploadAnimation) &&
            uploadStatus === 'idle'
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none',
        )}
      >
        <div className="absolute inset-0 -mt-2 z-[1001] pointer-events-none bg-white/90 animate-zoomIn" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full z-[1002] pointer-events-none flex flex-col items-center animate-zoomIn">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default UploadDropZone;
